import { Action } from "../Actions";
import { ContactTypes } from "../../constants";
import { findActiveContactIndex } from "./helpers";
import { isNumber } from "lodash";
import moment from "moment";

const LOAD_CUSTOMER_CONTACTS = "LOAD_CUSTOMER_CONTACTS";
const SET_ACTIVE_CUSTOMER_CONTACT = "SET_ACTIVE_CUSTOMER_CONTACT";
const TYPE_CUSTOMER_SEARCH_STRING = "TYPE_CUSTOMER_SEARCH_STRING";
const SET_ACTIVE_CUSTOMER_CONTACT_BY_SYSTEM_ID =
  "SET_ACTIVE_CUSTOMER_CONTACT_BY_SYSTEM_ID";

const CHANGE_VISIBILITY = "CHANGE_VISIBILITY";
const CUSTOMER_LOAD_MESSAGES = "CUSTOMER_LOAD_MESSAGES";
const MESSAGE_WAS_SENT = "MESSAGE_WAS_SENT";

const CUSTOMER_UPDATE_INCOMING_COUNTER = "CUSTOMER_UPDATE_INCOMING_COUNTER";
const CUSTOMER_UPDATE_MESSAGE_STATUS = "CUSTOMER_UPDATE_MESSAGE_STATUS";

// Come from MessagesActions
const CHANGE_CONTACT_TYPE = "CHANGE_CONTACT_TYPE";

export interface ICustomerContactsState {
  contacts: any[];

  /*
    {
        <contactId> : <counterValue>,
        <contactId> : <counterValue>,
        ...
        <contactId> : <counterValue>,
    }
    */
  incomingCounters: any;
  allCustomerIncomingMessages: number;

  activeContact: any;
  activeContactIndex: number;
  serachPhrase: string;
  visibilityChat: boolean;
  chat: any[];
  chatLoadingIndicator: boolean;
}

const initialState: ICustomerContactsState = {
  contacts: [],
  incomingCounters: {},
  allCustomerIncomingMessages: 0,
  activeContactIndex: -1,
  activeContact: null,
  serachPhrase: "",
  visibilityChat: false,
  chat: [],
  chatLoadingIndicator: false,
};

const sortContactFunc = function (a: any, b: any) {
  const aDate = a.original.msg_date || null;
  const bDate = b.original.msg_date || null;

  if (aDate === null && bDate === null) return 0;
  if (aDate !== null && bDate === null) return -1;
  if (aDate === null && bDate !== null) return 1;

  if (moment(aDate).isAfter(bDate)) return -1;
  if (moment(aDate).isBefore(bDate)) return 1;

  return 0;
};

export class CustomerContactsActions {
  static loadContacts = (contacts: any[]): Action => ({
    type: LOAD_CUSTOMER_CONTACTS,
    payload: { contacts: contacts },
  });
  static setActiveContact = (contact: any, index: number): Action => ({
    type: SET_ACTIVE_CUSTOMER_CONTACT,
    payload: { contact: contact, index: index },
  });
  static setActiveBySystemId = (systemId: number): Action => ({
    type: SET_ACTIVE_CUSTOMER_CONTACT_BY_SYSTEM_ID,
    payload: { systemId: systemId },
  });
  static typeSearch = (phrase: string): Action => ({
    type: TYPE_CUSTOMER_SEARCH_STRING,
    payload: { phrase: phrase },
  });
  static changeVisibility = (): Action => ({
    type: CHANGE_VISIBILITY,
    payload: {},
  });
  static loadMessages = (messagesForContact: {
    contact: any;
    messages: any[];
  }): Action => ({
    type: CUSTOMER_LOAD_MESSAGES,
    payload: { messagesForContact: messagesForContact },
  });
  static messageWasSent = (): Action => ({
    type: MESSAGE_WAS_SENT,
    payload: {},
  });
  static updateIncomingCounter = (incomingMessage: any): Action => ({
    type: CUSTOMER_UPDATE_INCOMING_COUNTER,
    payload: { incomingMessage },
  });
  static customerUpdateMessageStatus = (updatedMessage: any): Action => ({
    type: CUSTOMER_UPDATE_MESSAGE_STATUS,
    payload: { updatedMessage },
  });
}

export function reduce(
  state: ICustomerContactsState = initialState,
  action: Action
) {
  switch (action.type) {
    case MESSAGE_WAS_SENT: {
      return {
        ...state,
        ...{
          chatLoadingIndicator: true,
        },
      };
    }

    // Reset contacts selection by change contact type
    case CHANGE_CONTACT_TYPE: {
      const contacts = state.contacts.map((it) => ({ ...it, active: false }));
      return {
        ...state,
        ...{
          chat: [],
          contacts: contacts,
          activeContact: null,
          activeContactIndex: -1,
          chatLoadingIndicator: false,
        },
      };
    }

    case CUSTOMER_LOAD_MESSAGES: {
      const { messages, contact } = action.payload?.messagesForContact;
      const targetContactIndex = state.contacts.findIndex(
        (it) => it.phone === contact.phone
      );
      const targetContactID = state.contacts[targetContactIndex]?.original?.id;

      if (targetContactIndex >= 0) {
        const lastMessage = messages[messages.length - 1] || {};
        const newContacts = state.contacts
          .map((it, index) => {
            if (index === targetContactIndex) {
              it.message = lastMessage.body || "";
              it.original.message = lastMessage.body || "";
              it.original.msg_date = lastMessage.date || "";

              return it;
            }
            return it;
          })
          .sort(sortContactFunc);

        const index = findActiveContactIndex(newContacts, targetContactID);

        return {
          ...state,
          ...{
            contacts: newContacts,
            chat: messages,

            // In case with searching contacts - we don't have ids for them and can't calculate index
            activeContactIndex: index >= 0 ? index : state.activeContactIndex,
            chatLoadingIndicator: false,
          },
        };
      }

      return state;
    }

    case CUSTOMER_UPDATE_MESSAGE_STATUS: {
      const updatedMessage = action.payload?.updatedMessage || {};
      const start = state.chat.length - 1;
      const end = 0;
      for (let i = start; i >= end; i--) {
        if (state.chat[i].id === updatedMessage.id) {
          state.chat[i].status = updatedMessage.status;
        }
      }

      return {
        ...state,
        ...{
          chat: state.chat.map((it) => ({ ...it })),
        },
      };
    }

    case LOAD_CUSTOMER_CONTACTS: {
      const contacts = action.payload?.contacts;
      const list = contacts
        .filter(
          (it: any) => isNumber(it.type) && it.type === ContactTypes.CUSTOMER
        )
        .map((it: any) => ({
          avatar: it.avatar,
          name: `${it.first_name} ${it.last_name}`,
          datetime: "--:-- AM",
          phone: it.phone,
          message: it.message,
          original: it,
          type: ContactTypes.CUSTOMER,
        }))
        .sort(sortContactFunc);

      return {
        ...state,
        ...{
          contacts: list,
        },
      };
    }

    case SET_ACTIVE_CUSTOMER_CONTACT: {
      const index = action.payload?.index;
      const incomingCounters = state.incomingCounters;
      const ln = state.contacts.length;
      let activeContact = null;
      const contacts = state.contacts.map((it, idx) => {
        if (idx === index) {
          activeContact = it;
          incomingCounters[it.original.id] = 0;
        }
        return {
          ...it,
          active: idx === index,
        };
      });
      const allCustomerIncomingMessages = Object.values(
        incomingCounters
      ).reduce<number>(
        (accumulator: number, current: any): number => accumulator + current,
        0
      );

      return {
        ...state,
        ...{
          contacts: contacts,
          incomingCounters: incomingCounters,
          allCustomerIncomingMessages: allCustomerIncomingMessages,
          activeContact: activeContact,
          activeContactIndex: index,
          chatLoadingIndicator: true,
        },
      };
    }

    case SET_ACTIVE_CUSTOMER_CONTACT_BY_SYSTEM_ID: {
      const systemId = action.payload?.systemId;
      const incomingCounters = state.incomingCounters;
      let index = null;
      let activeContact = null;
      const contacts = state.contacts.map((it, idx) => {
        if (it.original.system_item_id === systemId) {
          activeContact = it;
          incomingCounters[it.original.id] = 0;
          index = idx;
        }
        return {
          ...it,
          active: it.original.system_item_id === systemId,
        };
      });
      const allCustomerIncomingMessages = Object.values(
        incomingCounters
      ).reduce<number>(
        (accumulator: number, current: any): number => accumulator + current,
        0
      );

      return {
        ...state,
        ...{
          contacts: contacts,
          incomingCounters: incomingCounters,
          allCustomerIncomingMessages: allCustomerIncomingMessages,
          activeContact: activeContact,
          activeContactIndex: index,
          chatLoadingIndicator: false,
        },
      };
    }

    case TYPE_CUSTOMER_SEARCH_STRING: {
      return {
        ...state,
        ...{
          serachPhrase: action.payload?.phrase || "",
        },
      };
    }

    case CHANGE_VISIBILITY: {
      return {
        ...state,
        ...{
          visibilityChat: !state.visibilityChat,
        },
      };
    }

    case CUSTOMER_UPDATE_INCOMING_COUNTER: {
      const incomingMessage = action.payload?.incomingMessage;
      const incSenderPhone = incomingMessage.sender;
      const incomingCounters = state.incomingCounters;
      const contacts = state.contacts
        .map((it, idx) => {
          if (it.phone === incSenderPhone) {
            incomingCounters[it.original.id] =
              (incomingCounters[it.original.id] || 0) + 1;
            it.message = incomingMessage.body || "";
            it.original.message = incomingMessage.body || "";
            it.original.msg_date = incomingMessage.date || "";
            return it;
          }
          return it;
        })
        .sort(sortContactFunc);

      const allCustomerIncomingMessages = Object.values(
        incomingCounters
      ).reduce<number>(
        (accumulator: number, current: any): number => accumulator + current,
        0
      );

      return {
        ...state,
        ...{
          contacts: contacts,
          incomingCounters: incomingCounters,
          allCustomerIncomingMessages: allCustomerIncomingMessages,
        },
      };
    }

    default:
      return state;
  }
}
